"use client"

import { useEffect, useRef, type ReactNode } from "react"
import styled from "@emotion/styled"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

/* EGDE-318: the prototype scrolls the page content as a white "sheet" with
   rounded top corners OVER the hero, which stays pinned behind it:
   - the hero sits in a sticky wrapper (top: 0, z-index 0), so it stays at the
     viewport top while the document scrolls;
   - everything after it is wrapped in an opaque sheet (z-index 1) whose top
     corners are rounded, so the pinned hero shows through the corner notches
     as the sheet rises.

   Designer round 2 (Raz, 2026-07-14): a plain 1:1 rise reads as "regular,
   continuous, boring scroll" — the references (Pango hero, Zoox "It's Not a
   Car") consume a longer scroll runway so the transition feels controlled and
   the section-2 parallax has room to play. Desktop now inserts a 100vh runway
   spacer between the hero and the sheet and counter-shifts the sheet by a
   scrubbed lift: the rise spends TWO viewports of scroll instead of one, and
   every scrub inside the sheet slows with it for free (they all key off
   getBoundingClientRect).

   EGDE-524: the original spec also auto-glided ("snapped") to an end of the
   runway after a short scroll pause, so the visitor was never parked
   mid-transition. QA filed the result as "unable to scroll properly" — any
   small wheel movement committed the page to a two-viewport jump. The snap is
   REMOVED: the scroll position alone drives the transition, and pausing
   anywhere (including half-hero) simply rests there.

   Fallback safety: --stage-progress defaults to 0, whose -100vh lift exactly
   cancels the runway spacer — without JS the page scrolls exactly like the
   pre-runway build. Reduced motion removes both the spacer and the lift.
   Mobile keeps the original 1:1 rise (no runway — momentum scrolling must
   never be hijacked on touch). */

const Stage = styled.div`
  width: 100%;
`

const HeroPin = styled.div`
  position: sticky;
  top: 0;
  z-index: 0;
  width: 100%;
`

const Runway = styled.div`
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
    height: 100vh;
  }

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`

const Sheet = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  background-color: ${colors.light.bg.primary};
  border-radius: ${sizes.borderRadius[24]}px ${sizes.borderRadius[24]}px 0 0;

  @media (min-width: ${breakpoints.lg}px) {
    border-radius: ${sizes.borderRadius[40]}px ${sizes.borderRadius[40]}px 0 0;
    /* Progress 0 lifts the sheet by exactly the runway height, cancelling the
       spacer; progress 1 rests at the natural document position. */
    transform: translate3d(
      0,
      calc(-100vh * (1 - var(--stage-progress, 0))),
      0
    );
  }

  /* EGDE-652: once the runway is spent the lift is a no-op translate — but a
     transform is a transform, so the sheet stayed on its own compositing layer
     for the rest of the visit and Chrome kept rendering EVERY block below the
     hero with grayscale antialiasing instead of the subpixel (ClearType) AA it
     gives untransformed text. On Windows that is what QA filed as "the text
     isn't sharp and uniform. Letters like ל appear with more ink than other
     letters". apply() stamps this attribute at progress 1, which is exactly
     where the declared transform already resolves to zero offset — so the
     sheet does not move a pixel, it just stops being a layer.
     (Not observable on macOS, which has no subpixel AA to lose.) */
  &[data-stage-settled] {
    @media (min-width: ${breakpoints.lg}px) {
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
  }
`

interface Props {
  hero: ReactNode
  children: ReactNode
}

export default function HeroScrollStage({ hero, children }: Props) {
  const stageRef = useRef<HTMLDivElement>(null)
  const runwayRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const stage = stageRef.current
    const runway = runwayRef.current
    const sheet = sheetRef.current
    if (!stage || !runway || !sheet) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    let raf = 0
    let lastProgress = -1

    const apply = () => {
      raf = 0
      const rect = stage.getBoundingClientRect()
      const total = runway.offsetHeight + window.innerHeight
      const p = Math.min(1, Math.max(0, -rect.top / total))
      /* Skip the style write once the progress saturates (0 or 1) — past the
         runway every scroll frame would otherwise invalidate style on the
         document-tall sheet subtree for a no-op. */
      if (p !== lastProgress) {
        lastProgress = p
        sheet.style.setProperty("--stage-progress", String(p))
        // EGDE-652: drop the (by now zero-offset) transform so the sheet stops
        // holding the whole page on a compositing layer. See the Sheet rule.
        sheet.toggleAttribute("data-stage-settled", p === 1)
      }
    }

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(apply)
    }

    apply()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <Stage ref={stageRef} data-hero-stage>
      <HeroPin>{hero}</HeroPin>
      <Runway ref={runwayRef} data-hero-runway aria-hidden />
      <Sheet ref={sheetRef}>{children}</Sheet>
    </Stage>
  )
}
